<script setup>
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';
import { defineEmits } from 'vue';

defineProps({
  title: {
    type: String,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: '',
  },
  emoji: {
    type: String,
    default: '',
  },
  isOpen: {
    type: Boolean,
    default: true,
  },
  showChevron: {
    type: Boolean,
    default: false,
  },
  animated: {
    type: Boolean,
    default: false,
  },
  draggable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['toggle']);

const onToggle = () => {
  emit('toggle');
};
</script>

<template>
  <div class="text-sm">
    <button
      type="button"
      class="flex items-center select-none w-full rounded-lg bg-n-slate-2 outline outline-1 outline-n-weak m-0 justify-between py-2 px-4"
      :class="[
        { 'rounded-bl-none rounded-br-none': isOpen },
        draggable ? 'cursor-grab drag-handle' : 'cursor-pointer',
      ]"
      :aria-expanded="isOpen"
      @click.stop="onToggle"
    >
      <div class="flex justify-between">
        <slot name="icon">
          <EmojiOrIcon class="inline-block w-5" :icon="icon" :emoji="emoji" />
        </slot>
        <h5 class="text-n-slate-12 text-sm mb-0 py-0 pr-2 pl-0">
          {{ title }}
        </h5>
      </div>
      <div class="flex flex-row">
        <slot name="button" />
        <div
          v-if="showChevron"
          class="!flex w-5 items-center justify-end text-n-slate-10"
        >
          <span
            class="i-lucide-chevron-right size-5 transition-transform duration-200 ease-out"
            :class="{ 'rotate-90': isOpen }"
          />
        </div>
        <div v-else class="flex justify-end w-3 text-n-blue-11 cursor-pointer">
          <fluent-icon v-if="isOpen" size="24" icon="subtract" type="solid" />
          <fluent-icon v-else size="24" icon="add" type="solid" />
        </div>
      </div>
    </button>
    <Transition
      :enter-active-class="
        animated
          ? 'overflow-hidden transition-[max-height,opacity] duration-200 ease-out'
          : ''
      "
      :enter-from-class="animated ? 'max-h-0 opacity-0' : ''"
      :enter-to-class="animated ? 'max-h-[2000px] opacity-100' : ''"
      :leave-active-class="
        animated
          ? 'overflow-hidden transition-[max-height,opacity] duration-200 ease-in'
          : ''
      "
      :leave-from-class="animated ? 'max-h-[2000px] opacity-100' : ''"
      :leave-to-class="animated ? 'max-h-0 opacity-0' : ''"
    >
      <div
        v-if="isOpen"
        class="outline outline-1 outline-n-weak -mt-[-1px] border-t-0 rounded-br-lg rounded-bl-lg"
        :class="compact ? 'p-0' : 'px-2 py-4'"
      >
        <slot />
      </div>
    </Transition>
  </div>
</template>
